import { useEffect, useState } from 'react';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  updateDoc,
} from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { Pencil, Plus, Star, Trash2, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { db, storage } from '../../firebase/config';
import { useAuth } from '../../hooks/useAuth';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { NativeSelect } from '../ui/select-native';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { cn } from '../../lib/utils';
import PageHeader from './shell/PageHeader';

const DIFFICULTY_LABELS = {
  1: 'Easy',
  2: 'Medium',
  3: 'Challenging',
  4: 'Hard',
  5: 'Very hard',
};

const EMPTY = {
  mapName: '',
  mapper: '',
  tier: 1,
  difficulty: 1,
  hintText: '',
  isActive: true,
};

const Field = ({ label, required, hint, children }) => (
  <div className="flex flex-col gap-2">
    <span className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">
      {label}
      {required && <span className="ml-1 text-heat">*</span>}
    </span>
    {children}
    {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
  </div>
);

const MapImageManagement = () => {
  const { user } = useAuth();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchImages = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, 'mapImages'), orderBy('createdAt', 'desc')));
      setImages(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      toast.error('Could not load images.', { description: err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImages();
  }, []);

  const resetForm = () => {
    setForm(EMPTY);
    setFile(null);
    setPreview(null);
    setEditingId(null);
  };

  const pickFile = (f) => {
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.mapName.trim()) {
      toast.error('Map name is required.');
      return;
    }
    if (!file && !editingId) {
      toast.error('Select an image file.');
      return;
    }
    setSaving(true);
    try {
      let imageUrl = '';
      let storageFilePath = '';

      if (editingId && !file) {
        const existing = images.find((i) => i.id === editingId);
        imageUrl = existing?.imageUrl || '';
        storageFilePath = existing?.storageFilePath || '';
      } else if (file) {
        const path = `map-images/${Date.now()}_${file.name}`;
        const sref = ref(storage, path);
        await uploadBytes(sref, file);
        imageUrl = await getDownloadURL(sref);
        storageFilePath = path;
      }

      const payload = {
        ...form,
        imageUrl,
        storageFilePath,
        addedBy: user?.uid || 'admin',
        updatedAt: new Date(),
      };

      if (editingId) {
        await updateDoc(doc(db, 'mapImages', editingId), payload);
        toast.success('Image updated.');
      } else {
        await addDoc(collection(db, 'mapImages'), { ...payload, createdAt: new Date() });
        toast.success('Image added.');
      }
      resetForm();
      await fetchImages();
    } catch (err) {
      toast.error('Save failed.', { description: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (image) => {
    setEditingId(image.id);
    setForm({
      mapName: image.mapName || '',
      mapper: image.mapper || '',
      tier: image.tier || 1,
      difficulty: image.difficulty || 1,
      hintText: image.hintText || '',
      isActive: image.isActive !== false,
    });
    setPreview(image.imageUrl);
    setFile(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteDoc(doc(db, 'mapImages', deleteTarget.id));
      if (deleteTarget.storageFilePath) {
        try {
          await deleteObject(ref(storage, deleteTarget.storageFilePath));
        } catch {
          // non-fatal
        }
      }
      toast.success('Image deleted.');
      setDeleteTarget(null);
      await fetchImages();
    } catch (err) {
      toast.error('Delete failed.', { description: err.message });
    }
  };

  const Stars = ({ level }) => (
    <span className="inline-flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={cn('h-3 w-3', i < level ? 'fill-heat text-heat' : 'text-muted-foreground/40')}
        />
      ))}
    </span>
  );

  return (
    <div className="pb-16">
      <PageHeader
        eyebrow="Maps & media"
        title="Map image library"
        description="Global pool of screenshots used by the Map Guessing minigame. Mark inactive to remove an image from play without deleting it."
      />

      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
        <Card className="border-border/60 bg-background/40">
          <CardHeader>
            <CardTitle className="font-display text-lg">
              {editingId ? 'Edit image' : 'Add image'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="grid gap-5">
              <Field label="Map name" required>
                <Input
                  value={form.mapName}
                  onChange={(e) => setForm({ ...form, mapName: e.target.value })}
                  required
                />
              </Field>

              <Field label="Mapper" hint="Author credit — unlockable as a hint in the daily game.">
                <Input
                  value={form.mapper}
                  onChange={(e) => setForm({ ...form, mapper: e.target.value })}
                  placeholder="e.g. GameChaos"
                />
              </Field>

              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Tier" hint="KZ map tier (1 easiest, 7 death).">
                  <NativeSelect
                    value={form.tier}
                    onChange={(e) => setForm({ ...form, tier: Number(e.target.value) })}
                  >
                    {[1, 2, 3, 4, 5, 6, 7].map((t) => (
                      <option key={t} value={t}>
                        Tier {t}
                      </option>
                    ))}
                  </NativeSelect>
                </Field>
                <Field label="Guess difficulty" hint="How hard is it to name this map?">
                  <NativeSelect
                    value={form.difficulty}
                    onChange={(e) => setForm({ ...form, difficulty: Number(e.target.value) })}
                  >
                    {[1, 2, 3, 4, 5].map((lvl) => (
                      <option key={lvl} value={lvl}>
                        {lvl} — {DIFFICULTY_LABELS[lvl]}
                      </option>
                    ))}
                  </NativeSelect>
                </Field>
              </div>

              <Field label="Active in game" hint="Inactive images stay stored but don't appear.">
                <div className="flex h-10 items-center gap-3">
                  <Switch
                    checked={form.isActive}
                    onCheckedChange={(v) => setForm({ ...form, isActive: v })}
                  />
                  <span className="text-sm text-muted-foreground">
                    {form.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </Field>

              <Field label="Hint (optional)">
                <Textarea
                  rows={2}
                  value={form.hintText}
                  onChange={(e) => setForm({ ...form, hintText: e.target.value })}
                />
              </Field>

              <Field label={editingId ? 'Replace image' : 'Image'}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => pickFile(e.target.files?.[0])}
                  className="block w-full text-sm file:mr-4 file:rounded-md file:border-0 file:bg-secondary file:px-3 file:py-2 file:text-xs file:font-medium file:uppercase file:tracking-[0.15em] file:text-secondary-foreground hover:file:bg-secondary/80"
                />
                {preview && (
                  <img
                    src={preview}
                    alt=""
                    className="mt-3 h-48 w-full rounded-md border border-border/60 object-cover"
                  />
                )}
              </Field>

              <div className="flex items-center gap-2 pt-2">
                <Button type="submit" disabled={saving}>
                  {editingId ? <Upload className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                  {saving ? 'Saving…' : editingId ? 'Update' : 'Add image'}
                </Button>
                {editingId && (
                  <Button type="button" variant="ghost" onClick={resetForm}>
                    <X className="h-4 w-4" />
                    Cancel edit
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>

        <section>
          <div className="mb-4 flex items-center gap-4">
            <span className="h-px w-6 bg-heat" />
            <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
              Library · {images.length}
            </h2>
          </div>

          {loading ? (
            <div className="divide-y divide-border/60 border-y border-border/60">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex h-20 animate-pulse items-center gap-4 px-2">
                  <div className="h-14 w-20 bg-muted" />
                  <div className="h-4 w-40 bg-muted" />
                  <div className="ml-auto h-3 w-20 bg-muted" />
                </div>
              ))}
            </div>
          ) : images.length === 0 ? (
            <div className="border border-dashed border-border/60 px-6 py-12 text-center text-sm text-muted-foreground">
              No images yet.
            </div>
          ) : (
            <ul className="divide-y divide-border/60 border-y border-border/60">
              {images.map((img) => (
                <li
                  key={img.id}
                  className="grid grid-cols-[auto_1fr_auto] items-center gap-4 px-2 py-3"
                >
                  <div className="h-14 w-20 shrink-0 overflow-hidden rounded-md border border-border/60 bg-muted/20">
                    <img src={img.imageUrl} alt="" className="h-full w-full object-cover" />
                  </div>
                  <div className="min-w-0">
                    <div className="truncate font-display text-base font-medium">{img.mapName}</div>
                    <div className="mt-1 flex items-center gap-3">
                      <Stars level={img.difficulty || 1} />
                      <Badge variant={img.isActive ? 'secondary' : 'outline'} className="uppercase">
                        {img.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(img)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setDeleteTarget(img)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete image?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Permanently removes{' '}
            <span className="font-medium text-foreground">{deleteTarget?.mapName}</span> from the
            library and from storage.
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MapImageManagement;
